import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShoppingCart, ExternalLink, Check, X } from "lucide-react";
import { Input, TextArea } from "./Warehouse";

export default function PurchaseRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = async () => setRows((await api.get("/purchase-requests")).data);
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Permintaan Barang</p>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Ajukan ke Bos</h1>
        </div>
        {user?.role !== "admin" && (
          <button data-testid="new-pr-btn" onClick={() => setShowForm(!showForm)} className="h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center gap-2 active:scale-[0.98] transition-transform">
            <ShoppingCart size={16} /> Ajukan
          </button>
        )}
      </div>

      {showForm && user?.role !== "admin" && (
        <PRForm onDone={() => { setShowForm(false); load(); }} />
      )}

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-slate-500">Belum ada permintaan.</p>}
        {rows.map((r) => <PRCard key={r.id} r={r} isAdmin={user?.role === "admin"} onDone={load} />)}
      </div>
    </div>
  );
}

function PRForm({ onDone }) {
  const [itemName, setItemName] = useState("");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("");
  const [link, setLink] = useState("");
  const [price, setPrice] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/purchase-requests", {
        item_name: itemName, qty: parseInt(qty), reason, product_link: link,
        estimated_price: price ? parseFloat(price) : null,
      });
      toast.success("Permintaan terkirim ke Bos");
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <h3 className="font-bold text-lg" style={{fontFamily:'Manrope'}}>Form Pengajuan</h3>
      <Input testid="pr-item" label="Nama Barang" value={itemName} onChange={setItemName} required />
      <div className="grid grid-cols-2 gap-3">
        <Input testid="pr-qty" label="Jumlah" type="number" value={qty} onChange={setQty} required />
        <Input testid="pr-price" label="Estimasi Harga (Rp)" type="number" value={price} onChange={setPrice} />
      </div>
      <Input testid="pr-link" label="Link Produk (Shopee/Tokopedia/dll)" type="url" value={link} onChange={setLink} />
      <TextArea testid="pr-reason" label="Alasan Kebutuhan" value={reason} onChange={setReason} />
      <button data-testid="pr-submit" type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl active:scale-[0.98] transition-transform">Kirim ke Bos</button>
    </form>
  );
}

function PRCard({ r, isAdmin, onDone }) {
  const decide = async (status) => {
    try {
      await api.patch(`/purchase-requests/${r.id}`, { status });
      toast.success(status === "approved" ? "Disetujui" : status === "purchased" ? "Ditandai sudah dibeli" : "Ditolak");
      onDone();
    } catch (err) { toast.error("Gagal"); }
  };
  const badge = {
    pending:  "bg-amber-100 text-amber-800",
    approved: "bg-blue-100 text-blue-800",
    rejected: "bg-red-100 text-red-800",
    purchased:"bg-emerald-100 text-emerald-800",
  }[r.status] || "bg-slate-100 text-slate-700";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${badge}`}>{r.status}</span>
          <p className="mt-2 font-bold text-slate-900" style={{fontFamily:'Manrope'}}>{r.item_name} <span className="text-slate-500 font-normal">× {r.qty}</span></p>
          {r.estimated_price && <p className="text-sm text-slate-600">Estimasi: Rp {Number(r.estimated_price).toLocaleString('id-ID')}</p>}
          <p className="text-xs text-slate-500 mt-1">Diminta oleh {r.requested_by_name} · {new Date(r.created_at).toLocaleString('id-ID')}</p>
          {r.reason && <p className="text-sm text-slate-700 mt-2">"{r.reason}"</p>}
          {r.product_link && (
            <a href={r.product_link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 font-semibold hover:underline">
              <ExternalLink size={14} /> Buka Link Produk
            </a>
          )}
        </div>
      </div>
      {isAdmin && r.status === "pending" && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid={`pr-approve-${r.id}`} onClick={() => decide("approved")} className="h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg flex items-center justify-center gap-1"><Check size={16}/> Setujui</button>
            <button data-testid={`pr-reject-${r.id}`} onClick={() => decide("rejected")} className="h-11 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg flex items-center justify-center gap-1"><X size={16}/> Tolak</button>
          </div>
          <p className="text-[11px] text-slate-500">Menyetujui otomatis mencatat pengeluaran (estimasi harga × qty) yang mengurangi profit.</p>
        </div>
      )}
      {isAdmin && r.status === "approved" && (
        <button data-testid={`pr-purchased-${r.id}`} onClick={() => decide("purchased")} className="mt-3 w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg">Tandai Sudah Dibeli</button>
      )}
    </div>
  );
}
